/**
 * KeyboardHintsV2 - Keyboard shortcuts help overlay
 *
 * Features:
 * - Shows all available keyboard shortcuts
 * - Toggleable display
 * - Grouped by category
 */

import { memo } from 'react'
import { useChallengeV2 as useChallengeStoreV2 } from '../../../hooks/useChallenge'

const shortcuts = [
  {
    category: 'Answers',
    items: [
      { key: '1-4', description: 'Select answer A-D' },
      { key: 'Ctrl + 1-4', description: 'Cross out answer A-D' }
    ]
  },
  {
    category: 'Confidence',
    items: [{ key: 'Shift + 1-5', description: 'Set confidence level' }]
  },
  {
    category: 'Navigation',
    items: [
      { key: 'Enter', description: 'Next question' },
      { key: 'Alt + Enter', description: 'Previous question' },
      { key: '← / →', description: 'Previous / Next question' },
      { key: 'Ctrl + R', description: 'Open review modal' }
    ]
  },
  {
    category: 'Actions',
    items: [
      { key: 'M', description: 'Mark for review' },
      { key: 'P', description: 'Pause / Resume test' },
      { key: 'Ctrl + F', description: 'Toggle focus mode' },
      { key: 'Ctrl + Enter', description: 'Submit stage' },
      { key: 'Escape', description: 'Close modal / Exit focus / Resume' }
    ]
  }
]

const KeyboardHintsV2 = memo(() => {
  const showKeyboardHints = useChallengeStoreV2((state) => state.showKeyboardHints)
  const toggleKeyboardHints = useChallengeStoreV2((state) => state.toggleKeyboardHints)

  if (!showKeyboardHints) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={toggleKeyboardHints}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-[modalIn_0.25s_ease-out]">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Keyboard Shortcuts</h2>
          <button
            onClick={toggleKeyboardHints}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Shortcuts list */}
        <div className="space-y-6">
          {shortcuts.map(({ category, items }) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                {category}
              </h3>
              <div className="space-y-2">
                {items.map(({ key, description }) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-gray-700 dark:text-gray-300">{description}</span>
                    <kbd className="px-2.5 py-1 text-sm font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded border border-gray-300 dark:border-gray-600">
                      {key}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
            Press{' '}
            <kbd className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded">?</kbd> to
            toggle this help
          </p>
        </div>
      </div>
    </div>
  )
})

KeyboardHintsV2.displayName = 'KeyboardHintsV2'

export default KeyboardHintsV2
